import { useState, useEffect, useCallback, useMemo } from 'react';
import { Mail, Phone, MapPin, Building2, Users, Lock } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { ResourceStatus, WorkingHoursProfile } from '@/types';
import { resourcesApi, ResourceCompanyAccessDto } from '@/services/api';
import { useCompany } from '@/contexts/CompanyContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { updateCompanyId } from '@/lib/auth-fetch';

interface Resource {
  id: string | number;
  name: string;
  email?: string;
  phone?: string;
  role: string;
  department?: string;
  location?: string;
  status: ResourceStatus;
  type: string;
  level?: string;
  photoUrl?: string;
  skills?: Array<{ name: string; category?: string; rating: number }>;
  useCustomWorkingProfile?: boolean;
  customWorkingProfile?: Record<string, any>;
  // Rate data from Resource Manager
  costRate?: number;
  billingRate?: number;
  overtimeRate?: number;
  rateCurrency?: string;
}

interface ResourceDetailsModalProps {
  resource: Resource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const COMPANY_STORAGE_KEY = 'ebscheduler_current_company';

export function ResourceDetailsModal({ resource, open, onOpenChange }: ResourceDetailsModalProps) {
  const { switchCompany, availableCompanies, refreshCompanies } = useCompany();
  const { canViewCompanies, canManageCompanies } = usePermissions();
  const canViewCompanyAccess = canViewCompanies();
  const canManageCompanyAccess = canManageCompanies();
  const [activeTab, setActiveTab] = useState('skills');
  const [companyAccess, setCompanyAccess] = useState<ResourceCompanyAccessDto[]>([]);
  const [companyAccessLoading, setCompanyAccessLoading] = useState(false);
  const [companyAccessSaving, setCompanyAccessSaving] = useState(false);
  // Snapshot of the server state after the last load/save so we can detect
  // unsaved changes by comparing against the current local state.
  const [savedCompanyAccess, setSavedCompanyAccess] = useState<ResourceCompanyAccessDto[]>([]);

  const loadCompanyAccess = useCallback(async (resourceId: string | number) => {
    setCompanyAccessLoading(true);
    try {
      const data = await resourcesApi.getCompanyAccess(resourceId);
      setCompanyAccess(data);
      setSavedCompanyAccess(data);
    } catch {
      setCompanyAccess([]);
      setSavedCompanyAccess([]);
    } finally {
      setCompanyAccessLoading(false);
    }
  }, []);

  const resourceId = resource?.id;
  const { settings } = useScheduler();

  useEffect(() => {
    if (open && resourceId && activeTab === 'company-access' && canViewCompanyAccess) {
      loadCompanyAccess(resourceId);
    }
  }, [open, resourceId, activeTab, canViewCompanyAccess, loadCompanyAccess]);

  // Detect unsaved changes by comparing current state against the saved snapshot.
  const hasUnsavedChanges = useMemo(() => {
    if (companyAccess.length !== savedCompanyAccess.length) return true;
    return companyAccess.some((c, i) => {
      const s = savedCompanyAccess[i];
      return c.companyId !== s.companyId || c.hasAccess !== s.hasAccess || c.isDefault !== s.isDefault;
    });
  }, [companyAccess, savedCompanyAccess]);

  // Local-only toggle — no API call
  const handleToggleAccess = (companyId: string, currentlyHasAccess: boolean) => {
    setCompanyAccess(prev =>
      prev.map(c =>
        c.companyId === companyId
          ? { ...c, hasAccess: !currentlyHasAccess, isDefault: !currentlyHasAccess ? c.isDefault : false }
          : c
      )
    );
  };

  // Local-only set-default toggle — no API call
  const handleSetDefault = (companyId: string, currentlyDefault: boolean) => {
    setCompanyAccess(prev =>
      prev.map(c => ({
        ...c,
        isDefault: c.companyId === companyId ? !currentlyDefault : false,
        hasAccess: c.companyId === companyId ? (!currentlyDefault ? true : c.hasAccess) : c.hasAccess,
      }))
    );
  };

  // Discard local changes and revert to the last saved state
  const handleDiscardChanges = () => {
    setCompanyAccess(savedCompanyAccess);
  };

  // Persist all changes to the server in one batch
  const handleSaveChanges = async () => {
    if (!resource) return;
    setCompanyAccessSaving(true);
    try {
      // Diff each company row against the saved snapshot
      for (const current of companyAccess) {
        const saved = savedCompanyAccess.find(s => s.companyId === current.companyId);
        if (!saved) continue;

        // Access changed
        if (current.hasAccess !== saved.hasAccess) {
          if (current.hasAccess) {
            await resourcesApi.setCompanyAccess(resource.id, current.companyId, current.isDefault);
          } else {
            await resourcesApi.removeCompanyAccess(resource.id, current.companyId);
          }
        } else if (current.hasAccess && current.isDefault !== saved.isDefault) {
          // Access unchanged but default flag changed
          if (current.isDefault) {
            await resourcesApi.setCompanyAccess(resource.id, current.companyId, true);
            await resourcesApi.setDefaultCompany(resource.id, current.companyId);
          } else {
            await resourcesApi.clearDefaultCompany(resource.id);
          }
        }
      }

      // Re-fetch authoritative state so the UI matches the server
      const data = await resourcesApi.getCompanyAccess(resource.id);
      setCompanyAccess(data);
      setSavedCompanyAccess(data);

      // Notify CompanyContext so the company dropdown updates immediately
      window.dispatchEvent(new CustomEvent('company-access-changed'));

      // If a new default was set, switch the company dropdown to it
      const newDefault = companyAccess.find(c => c.isDefault);
      const oldDefault = savedCompanyAccess.find(s => s.isDefault);
      if (newDefault && newDefault.companyId !== oldDefault?.companyId) {
        localStorage.setItem(COMPANY_STORAGE_KEY, newDefault.companyId);
        updateCompanyId(newDefault.companyId);
        const target = availableCompanies.find(c => String(c.id).toLowerCase() === newDefault.companyId.toLowerCase());
        if (target) {
          switchCompany(String(target.id));
        } else {
          refreshCompanies();
        }
      }
    } catch {
      // On failure, re-fetch to restore authoritative state
      await loadCompanyAccess(resource.id);
    } finally {
      setCompanyAccessSaving(false);
    }
  };

  if (!resource) return null;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getStatusLabel = (status: ResourceStatus) => {
    switch (status) {
      case 'available':
      case 'busy':
      case 'active':
        return 'Active';
      case 'off':
      case 'inactive':
        return 'Inactive';
      default:
        return 'Active';
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className={`text-lg ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}>
            ★
          </span>
        ))}
      </div>
    );
  };

  // Rate data from Resource Manager (via PSA Resources webhook)
  const currency = resource.rateCurrency || 'GBP';
  const currencySymbol = currency === 'GBP' ? '\u00A3' : currency === 'USD' ? '$' : currency === 'EUR' ? '\u20AC' : currency;
  const costRateHour = resource.costRate;
  const billRateHour = resource.billingRate;
  const overtimeRateHour = resource.overtimeRate;
  const costRateDay = costRateHour != null ? costRateHour * 8 : undefined;
  const billRateDay = billRateHour != null ? billRateHour * 8 : undefined;
  const overtimeRateDay = overtimeRateHour != null ? overtimeRateHour * 8 : undefined;
  const hasRates = costRateHour != null || billRateHour != null;

  // Build working pattern from the resource's customWorkingProfile or the global profile.
  // The backend stores per-day hours as { "monday": 7.5, "tuesday": 9, … }.
  const getWorkingPattern = () => {
    const profile: WorkingHoursProfile = resource.customWorkingProfile
      ? resource.customWorkingProfile as WorkingHoursProfile
      : settings.globalWorkingProfile;

    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
    const displayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const days = dayNames.map((key, i) => {
      const hours = typeof profile[key] === 'number' ? profile[key] : 0;
      return { name: displayNames[i], enabled: hours > 0, hours };
    });

    const totalHours = days.reduce((sum, d) => sum + d.hours, 0);
    const workingDays = days.filter(d => d.enabled).length;
    const avgPerDay = workingDays > 0 ? totalHours / workingDays : 0;

    return { totalHours, days, workingDays, avgPerDay };
  };

  const workingPattern = getWorkingPattern();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] p-0 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-4 border-b">
          <Users className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-base font-semibold">Resource Details</h2>
        </div>

        {/* Profile Section */}
        <div className="px-5 py-4 border-b">
          <div className="flex items-start gap-4">
            <Avatar className="h-[76px] w-[76px]">
              <AvatarImage src={resource.photoUrl} alt={resource.name} />
              <AvatarFallback className="text-lg">{getInitials(resource.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h2 className="text-lg font-semibold leading-tight">{resource.name}</h2>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={`text-white text-xs px-2 py-0.5 ${
                    getStatusLabel(resource.status) === 'Active'
                      ? 'bg-teal-600 hover:bg-teal-700'
                      : 'bg-zinc-500 hover:bg-zinc-600'
                  }`}>
                    {getStatusLabel(resource.status)}
                  </Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{resource.role}</p>
            </div>
          </div>

          {/* Contact Info - 2 column grid */}
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-4 w-4 flex-shrink-0" />
              <span className="truncate text-xs">{resource.email || '-'}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs">{resource.phone || '-'}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs">{resource.location || '-'}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Building2 className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs">{resource.department || '-'}</span>
            </div>
          </div>
        </div>

        {/* Tabs - Contained with rounded corners */}
        <div className="flex-1 flex flex-col min-h-0 px-5 py-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
            <TabsList className="w-full justify-start h-auto p-0.5 bg-gray-100 rounded-lg mb-4">
              <TabsTrigger 
                value="skills" 
                className="flex-1 px-2 py-1 text-xs font-normal text-gray-600 data-[state=active]:text-black data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              >
                Skills
              </TabsTrigger>
              <TabsTrigger 
                value="rates" 
                className="flex-1 px-2 py-1 text-xs font-normal text-gray-600 data-[state=active]:text-black data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              >
                Rates
              </TabsTrigger>
              <TabsTrigger 
                value="working-pattern" 
                className="flex-1 px-2 py-1 text-xs font-normal text-gray-600 data-[state=active]:text-black data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              >
                Working Pattern
              </TabsTrigger>
              <TabsTrigger
                value="company-access"
                className="flex-1 px-2 py-1 text-xs font-normal text-gray-600 data-[state=active]:text-black data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              >
                Company Access
              </TabsTrigger>
            </TabsList>

            {/* Skills Tab */}
            <TabsContent value="skills" className="flex-1 overflow-y-auto mt-0 space-y-3">
              {resource.skills && resource.skills.length > 0 ? (
                resource.skills.map((skill) => (
                  <div key={skill.name} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-2.5">
                      <span className="text-sm font-medium">{skill.name}</span>
                      {skill.category && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 text-xs px-2 py-0.5 font-normal">
                          {skill.category}
                        </Badge>
                      )}
                    </div>
                    {renderStars(skill.rating)}
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-muted-foreground text-sm">No skills data available</div>
                  <div className="text-muted-foreground text-xs mt-1">Skills are not currently sent via the webhook</div>
                </div>
              )}
            </TabsContent>

            {/* Rates Tab */}
            <TabsContent value="rates" className="flex-1 overflow-y-auto mt-0 space-y-4">
              {hasRates ? (
                <>
                  {/* Cost Rate Card */}
                  {costRateHour != null && (
                    <div className="rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-5 w-5 rounded-full bg-gray-100 flex items-center justify-center">
                          <span className="text-xs">C</span>
                        </div>
                        <h3 className="text-sm font-semibold">Cost Rate</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Day Rate</div>
                          <div className="text-2xl font-semibold">{currencySymbol}{costRateDay?.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Hour Rate</div>
                          <div className="text-2xl font-semibold">{currencySymbol}{costRateHour.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Bill Rate Card */}
                  {billRateHour != null && (
                    <div className="rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-5 w-5 rounded-full bg-gray-100 flex items-center justify-center">
                          <span className="text-xs">B</span>
                        </div>
                        <h3 className="text-sm font-semibold">Bill Rate</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Day Rate</div>
                          <div className="text-2xl font-semibold">{currencySymbol}{billRateDay?.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Hour Rate</div>
                          <div className="text-2xl font-semibold">{currencySymbol}{billRateHour.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Overtime Rate Card */}
                  {overtimeRateHour != null && (
                    <div className="rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-5 w-5 rounded-full bg-gray-100 flex items-center justify-center">
                          <span className="text-xs">O</span>
                        </div>
                        <h3 className="text-sm font-semibold">Overtime Rate</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Day Rate</div>
                          <div className="text-2xl font-semibold">{currencySymbol}{overtimeRateDay?.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">Hour Rate</div>
                          <div className="text-2xl font-semibold">{currencySymbol}{overtimeRateHour.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-muted-foreground text-sm">No rate data available</div>
                  <div className="text-muted-foreground text-xs mt-1">Rates will appear once synced from Resource Manager</div>
                </div>
              )}
            </TabsContent>

            {/* Working Pattern Tab */}
            <TabsContent value="working-pattern" className="flex-1 overflow-y-auto mt-0">
              {/* Header with Total Hours */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold mb-2">Working Pattern</h3>
                  <Badge className="bg-teal-600 text-white hover:bg-teal-700 text-xs px-2.5 py-0.5">
                    {resource.useCustomWorkingProfile ? 'Custom' : 'Default'}
                  </Badge>
                </div>
                <div className="text-right border border-gray-200 rounded-lg px-4 py-2">
                  <div className="text-xs text-muted-foreground mb-1">Total Hours</div>
                  <div className="text-2xl font-semibold text-teal-600">{workingPattern.totalHours.toFixed(2)} h</div>
                </div>
              </div>

              {/* Working Days & Hours Section */}
              <div className="mb-6">
                <h4 className="text-xs font-semibold mb-3">Working Days & Hours</h4>
                
                {/* Table */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-3 px-6 py-2 bg-gray-50 border-b border-gray-200">
                    <div className="text-xs font-medium text-gray-600">Day</div>
                    <div className="text-xs font-medium text-gray-600 text-center">Status</div>
                    <div className="text-xs font-medium text-gray-600 text-center">Hours</div>
                  </div>

                  {/* Table Rows */}
                  {workingPattern.days.map((day, index) => (
                    <div 
                      key={day.name} 
                      className={`grid grid-cols-3 px-6 py-2 items-center ${
                        index !== workingPattern.days.length - 1 ? 'border-b border-gray-200' : ''
                      }`}
                    >
                      <div className="text-sm">{day.name}</div>
                      <div className="flex justify-center">
                        <Switch checked={day.enabled} disabled className="pointer-events-none" />
                      </div>
                      <div className="text-sm text-center">{day.hours.toFixed(2)} h</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-xs text-teal-600 mb-1">Working Days</div>
                  <div className="text-xl font-semibold">{workingPattern.workingDays}</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-teal-600 mb-1">Total Hours</div>
                  <div className="text-xl font-semibold">{workingPattern.totalHours.toFixed(2)} h</div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-teal-600 mb-1">Avg per Day</div>
                  <div className="text-xl font-semibold">{workingPattern.avgPerDay.toFixed(2)} h</div>
                </div>
              </div>
            </TabsContent>

            {/* Company Access Tab */}
            <TabsContent value="company-access" className="flex-1 overflow-y-auto mt-0">
              {!canViewCompanyAccess ? (
                <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                  <Lock className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">You do not have permission</p>
                  <p className="text-xs text-muted-foreground">
                    You don't have permission to view this resource's company access.
                  </p>
                </div>
              ) : (
              <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold">Company Access</h3>
                {hasUnsavedChanges && canManageCompanyAccess && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleDiscardChanges}
                      disabled={companyAccessSaving}
                      className="text-xs px-3 py-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Discard
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveChanges}
                      disabled={companyAccessSaving}
                      className="text-xs px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700 transition-colors disabled:opacity-50"
                    >
                      {companyAccessSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
              {companyAccessLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-muted-foreground text-sm">Loading companies...</div>
                </div>
              ) : companyAccess.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-muted-foreground text-sm">No companies available</div>
                  <div className="text-muted-foreground text-xs mt-1">Companies will appear once they are created in Settings</div>
                </div>
              ) : (
                <div className="space-y-0">
                  {companyAccess.map((company) => (
                    <div
                      key={company.companyId}
                      className={`flex items-center justify-between py-4 px-1 border-b border-gray-100 ${
                        !company.hasAccess ? 'opacity-60' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Building2 className="h-5 w-5 text-gray-500" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{company.companyName}</div>
                          <div className="text-xs text-muted-foreground">{company.companyCode}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {company.hasAccess && (
                          <button
                            type="button"
                            onClick={() => handleSetDefault(company.companyId, company.isDefault)}
                            disabled={companyAccessSaving || !canManageCompanyAccess}
                            className={`text-xs px-2 py-0.5 rounded transition-colors disabled:opacity-50 ${
                              company.isDefault
                                ? 'text-teal-700 bg-teal-50 font-medium'
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            Set as default
                          </button>
                        )}
                        <Switch
                          checked={company.hasAccess}
                          onCheckedChange={() => handleToggleAccess(company.companyId, company.hasAccess)}
                          disabled={companyAccessSaving || !canManageCompanyAccess}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}
